import { DashboardCard } from "@dashboard/components/Card";
import HorizontalSpacer from "@dashboard/components/HorizontalSpacer";
import Money from "@dashboard/components/Money";
import { OrderAction, OrderDetailsFragment, OrderStatus } from "@dashboard/graphql";
import { buttonMessages } from "@dashboard/intl";
import { getDiscountTypeLabel } from "@dashboard/orders/utils/data";
import { OrderDetailsViewModel } from "@dashboard/orders-v2/order-details-view-model";
import { Box, Button, Divider, Skeleton, sprinkles } from "@saleor/macaw-ui-next";
import clsx from "clsx";
import { FormattedMessage, useIntl } from "react-intl";

import { OrderPaymentStatusPill } from "../OrderPaymentSummaryCard/components/OrderPaymentStatusPill";
import { OrderShippingPriceEdit } from "../OrderShippingPriceEdit/OrderShippingPriceEdit";
import { OrderUsedGiftCards } from "../OrderUsedGiftCards";
import { orderPaymentMessages, paymentButtonMessages } from "./messages";
import { useStyles } from "./styles";
import { extractRefundedAmount, getDiscountAmount } from "./utils";

interface OrderPaymentProps {
  order: OrderDetailsFragment;
  onCapture: () => void;
  onRefund: () => void;
  onVoid: () => void;
  onShippingMethodEdit?: () => void;
}

const OrderPayment = (props: OrderPaymentProps) => {
  const { order, onCapture, onRefund, onVoid, onShippingMethodEdit } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const canEditShippingMethod = !!onShippingMethodEdit && order?.status !== OrderStatus.CANCELED;
  const paymentMethod = order?.metadata?.find(
    metadataItem => metadataItem.key === "paymentMethod",
  )?.value;
  const canCapture = (order?.actions ?? []).includes(OrderAction.CAPTURE);
  const canVoid = (order?.actions ?? []).includes(OrderAction.VOID);
  const canRefund = (order?.actions ?? []).includes(OrderAction.REFUND);
  const refundedAmount = extractRefundedAmount(order);
  const usedGiftCardAmount = order ? OrderDetailsViewModel.getGiftCardsAmountUsed(order) : null;
  const usedGiftCards = order?.giftCards
    ? OrderDetailsViewModel.getUsedGiftCards(order.giftCards)
    : null;

  const getDeliveryMethodName = (order: OrderDetailsFragment) => {
    if (
      order?.shippingMethodName === undefined &&
      order?.shippingPrice === undefined &&
      order?.collectionPointName === undefined
    ) {
      return <Skeleton />;
    }

    if (order.shippingMethodName === null) {
      return order.collectionPointName == null ? (
        <FormattedMessage {...orderPaymentMessages.shippingDoesNotApply} />
      ) : (
        <FormattedMessage {...orderPaymentMessages.clickAndCollectShippingMethod} />
      );
    }

    return order.shippingMethodName;
  };

  return (
    <DashboardCard data-test-id="OrderPayment">
      <DashboardCard.Header>
        <DashboardCard.Title>
          <FormattedMessage {...orderPaymentMessages.paymentTitle} />

          <OrderPaymentStatusPill
            order={order}
            className={sprinkles({
              marginLeft: 2,
              marginRight: "auto",
            })}
          />

          {!!paymentMethod && (
            <DashboardCard.Subtitle>
              <FormattedMessage
                {...orderPaymentMessages.paymentMethod}
                values={{ paymentMethod }}
              />
            </DashboardCard.Subtitle>
          )}
        </DashboardCard.Title>

        <DashboardCard.Toolbar>
          {!order?.paymentStatus ? (
            <Skeleton />
          ) : (
            <div className={classes.titleContainer}>
              {order?.status !== OrderStatus.CANCELED && (canCapture || canRefund || canVoid) && (
                <div className={classes.actions}>
                  {canCapture && (
                    <Button variant="secondary" onClick={onCapture}>
                      <FormattedMessage {...paymentButtonMessages.capture} />
                    </Button>
                  )}
                  {canRefund && (
                    <Button variant="secondary" onClick={onRefund} data-test-id="refund-button">
                      <FormattedMessage {...paymentButtonMessages.refund} />
                    </Button>
                  )}
                  {canVoid && (
                    <Button variant="secondary" onClick={onVoid}>
                      <FormattedMessage {...paymentButtonMessages.void} />
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content className={classes.payments}>
        <div className={classes.root}>
          {order?.discounts?.map(discount => (
            <div key={discount.id}>
              <FormattedMessage {...orderPaymentMessages.discount} />
              <HorizontalSpacer spacing={4} />
              <span className={classes.supportText}>{getDiscountTypeLabel(discount, intl)}</span>
              <span
                className={clsx(
                  classes.leftmostRightAlignedElement,
                  classes.smallFont,
                  classes.supportText,
                )}
              >
                <FormattedMessage {...orderPaymentMessages.includedInSubtotal} />
              </span>
              <HorizontalSpacer spacing={2} />
              <div className={classes.supportText}>
                <Money money={getDiscountAmount(discount.amount)} />
              </div>
            </div>
          ))}
          <div>
            <FormattedMessage {...orderPaymentMessages.subtotal} />
            <div className={classes.leftmostRightAlignedElement}>
              {order?.subtotal.gross ? <Money money={order.subtotal.gross} /> : <Skeleton />}
            </div>
          </div>
          <div>
            <FormattedMessage {...orderPaymentMessages.shipping} />
            <HorizontalSpacer spacing={4} />
            <div className={classes.supportText}>{getDeliveryMethodName(order)}</div>
            {canEditShippingMethod && (
              <>
                <HorizontalSpacer spacing={2} />
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={onShippingMethodEdit}
                  data-test-id="edit-shipping-method"
                >
                  <FormattedMessage {...buttonMessages.edit} />
                </Button>
              </>
            )}
            <div className={classes.leftmostRightAlignedElement}>
              {order?.shippingPrice.gross ? (
                <Box display="inline-flex" alignItems="center" gap={1}>
                  {/* Same orders whose shipping method staff may change (unpaid, unfulfilled). */}
                  {canEditShippingMethod && order.shippingMethod && (
                    <OrderShippingPriceEdit order={order} />
                  )}
                  <Money money={order.shippingPrice.gross} />
                </Box>
              ) : (
                <Skeleton />
              )}
            </div>
          </div>
          <div className={classes.taxesRow}>
            <FormattedMessage {...orderPaymentMessages.taxes} />
            {order?.total.tax.amount > 0 && (
              <>
                <div
                  className={clsx(
                    classes.supportText,
                    classes.smallFont,
                    classes.leftmostRightAlignedElement,
                  )}
                >
                  <FormattedMessage {...orderPaymentMessages.includedInPrices} />{" "}
                </div>
                <HorizontalSpacer spacing={2} />
              </>
            )}
            <div
              className={clsx(
                {
                  [classes.leftmostRightAlignedElement]: order?.total.tax.amount === 0,
                },
                classes.supportText,
              )}
            >
              {order?.total.tax ? <Money money={order.total.tax} /> : <Skeleton />}
            </div>
          </div>
          <div className={classes.totalRow}>
            <FormattedMessage {...orderPaymentMessages.total} />
            <div className={classes.leftmostRightAlignedElement}>
              {order?.total.gross ? <Money money={order.total.gross} /> : <Skeleton />}
            </div>
          </div>
        </div>
      </DashboardCard.Content>
      <Divider />
      <DashboardCard.Content className={classes.payments}>
        <div className={classes.root}>
          {!!usedGiftCardAmount && usedGiftCards && (
            <div>
              <OrderUsedGiftCards giftCards={usedGiftCards} />
              <div className={classes.leftmostRightAlignedElement}>
                <Money
                  money={{
                    amount: usedGiftCardAmount,
                    currency: order?.total?.gross?.currency,
                  }}
                />
              </div>
            </div>
          )}
          <div>
            <FormattedMessage {...orderPaymentMessages.preauthorized} />
            <div className={classes.leftmostRightAlignedElement}>
              {order?.totalAuthorized ? <Money money={order.totalAuthorized} /> : <Skeleton />}
            </div>
          </div>
          <div>
            <FormattedMessage {...orderPaymentMessages.captured} />
            <div className={classes.leftmostRightAlignedElement}>
              {order?.totalCaptured ? <Money money={order.totalCaptured} /> : <Skeleton />}
            </div>
          </div>
          {!!refundedAmount?.amount && (
            <div>
              <FormattedMessage {...orderPaymentMessages.refunded} />
              <div className={classes.leftmostRightAlignedElement}>
                {<Money money={refundedAmount} />}
              </div>
            </div>
          )}
          <div
            className={clsx(
              { [classes.success]: order?.totalBalance.amount === 0 },
              classes.totalRow,
            )}
          >
            <FormattedMessage {...orderPaymentMessages.outstanding} />
            <div
              className={classes.leftmostRightAlignedElement}
              data-test-id="order-balance-status"
            >
              {order?.totalBalance.amount === 0 ? (
                <FormattedMessage {...orderPaymentMessages.settled} />
              ) : order?.totalBalance ? (
                <Money money={order.totalBalance} />
              ) : (
                <Skeleton />
              )}
              {}
            </div>
          </div>
        </div>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

OrderPayment.displayName = "OrderPayment";
export default OrderPayment;
